import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import Box from 'carbon-react/esm/components/box';
import Typography from 'carbon-react/esm/components/typography';
import Icon from 'carbon-react/esm/components/icon';
import Loader from 'carbon-react/lib/components/loader/__next__';
import Button from 'carbon-react/esm/components/button/__next__';
import { useQueryBilanDetail } from '../../../hooks/useQueryBilanDetail';
import { parseQueryBilanDetailResponse } from '../../../utils/odataResponseHelper';
import { useBilanContent } from '../hooks/useBilanContent';

export default function BilanDetailContent() {
  const { instanceId, bilanId } = useParams<{ instanceId: string; bilanId: string }>();
  const navigate = useNavigate();
  const bilanQueryResult = useQueryBilanDetail(bilanId);
  const { data: bilan } = parseQueryBilanDetailResponse(bilanQueryResult);

  const isBilanEmpty = !bilan || Object.keys(bilan).length === 0;
  const contentState = useBilanContent(bilan, !isBilanEmpty && Boolean(bilanId));

  if (bilanQueryResult.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Loader size="large" />
      </Box>
    );
  }

  if (isBilanEmpty) {
    // Id de bilan absent de la liste (supprimé, tronqué ou bricolé dans l'URL) :
    // on retombe sur le détail de la déclaration plutôt que d'afficher un cul-de-sac.
    return <Navigate to={`/dsn-list/${instanceId}/bilans`} replace />;
  }

  if (contentState.status === 'loading') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Loader size="large" />
      </Box>
    );
  }

  if (contentState.status === 'error') {
    return (
      <Box
        bg="white"
        minHeight={250}
        padding="32px"
        gap="5px"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        margin="0 auto"
      >
        <Box display="flex" flexDirection="row" alignItems="center">
          <span style={{ color: 'var(--colorsSemanticNegative500)' }}>
            <Icon type="error" size="medium" marginBottom="16px" />
          </span>
          <Typography variant="h3" marginBottom="16px">
            {contentState.message}
          </Typography>
        </Box>
        <Link to={`/dsn-list/${instanceId}/bilans`}>Retour à la déclaration</Link>
      </Box>
    );
  }

  if (contentState.status === 'empty') {
    return (
      <Box
        bg="white"
        minHeight={250}
        padding="32px"
        gap="5px"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        margin="0 auto"
      >
        <Box display="flex" flexDirection="row" alignItems="center">
          <span style={{ color: 'var(--colorsSemanticInfo500)' }}>
            <Icon type="info" size="medium" marginBottom="16px" />
          </span>
          <Typography variant="h3" marginBottom="16px">
            Ce bilan ne contient aucune donnée.
          </Typography>
        </Box>
        <Link to={`/dsn-list/${instanceId}/bilans`}>Retour à la déclaration</Link>
      </Box>
    );
  }

  if (contentState.status === 'zip-downloaded') {
    return (
      <Box
        bg="white"
        minHeight={250}
        padding="32px"
        gap="16px"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        margin="0 auto"
      >
        <Typography variant="h3">Le téléchargement du bilan a démarré.</Typography>
        <Button onClick={() => navigate(`/dsn-list/${instanceId}/bilans`)}>
          Retour à la déclaration
        </Button>
      </Box>
    );
  }

  return (
    <Box position="relative" height="100vh" width="100%" margin="0" p="0">
      <Box
        position="absolute"
        top="50px"
        left="12px"
        bg="white"
        padding="6px 12px"
        borderRadius="borderRadius050"
        boxShadow="boxShadow050"
        display="flex"
        alignItems="center"
      >
        <Link to={`/dsn-list/${instanceId}/bilans`}>← Retour à la déclaration</Link>
      </Box>
      {contentState.kind === 'pdf' ? (
        <embed
          src={contentState.blobUrl}
          type="application/pdf"
          width="100%"
          height="100%"
          title="Bilan"
        />
      ) : (
        <iframe
          src={contentState.blobUrl}
          // allow-scripts (ex: le "Afficher plus/moins" du backend) sans allow-same-origin :
          // le document garde une origine opaque, donc pas d'accès cookies/storage/parent
          // même si son script tournait mal. Ne jamais combiner les deux flags sur un
          // même iframe : allow-scripts + allow-same-origin annule l'intérêt du sandbox.
          sandbox="allow-scripts"
          title="Bilan"
          style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
        />
      )}
    </Box>
  );
}
